<template>
	<div id="content" class="content mypage">
		<div class="container">
			<!-- 페이지 타이틀 -->
			<div class="page-title">
				<h2 class="tit">오프라인 인증 신청 내역</h2>
			</div>

			<!-- 필터 영역 -->
			<div v-if="!loading" class="filter-area">
				<div class="filter-row">
					<div class="filter-item">
						<label for="product-filter">제품명</label>
						<select id="product-filter" v-model="filters.productName" class="filter-select">
							<option value="">전체</option>
							<option v-for="productName in productNames" :key="productName" :value="productName">
								{{ productName }}
							</option>
						</select>
					</div>
					<div class="filter-item">
						<label for="status-filter">승인 상태</label>
						<select id="status-filter" v-model="filters.approvalStatus" class="filter-select">
							<option value="">전체</option>
							<option value="APPROVED">승인</option>
							<option value="PENDING">승인 대기</option>
							<option value="REJECTED">거부</option>
							<option value="CANCELLED">철회</option>
						</select>
					</div>
				</div>
			</div>

			<!-- 로딩 표시 -->
			<div v-if="loading" class="loading-message">
				<p>신청 내역을 불러오는 중...</p>
			</div>

			<!-- 신청 내역 목록 -->
			<div v-else class="certification-history-list">
				<ul v-if="historyList.length > 0">
					<li v-for="item in historyList" :key="item.id" class="history-item">
						<div class="date-wrap">
							{{ formatDateTime(item.createdAt) }}
						</div>
						<div class="content-wrap">
							<div class="info-box">
								<div class="info-row">
									<strong>제품명</strong>
									<div>{{ item.productName }}</div>
								</div>
								<div class="info-row">
									<strong>시리얼 번호</strong>
									<div>{{ item.serialNumber }}</div>
								</div>
								<div class="info-row">
									<strong>승인 상태</strong>
									<div :class="getStatusClass(item.approvalStatus)">
										{{ getStatusLabel(item.approvalStatus) }}
									</div>
								</div>
								<div class="info-row" v-if="item.endDate">
									<strong>유효기간 종료일</strong>
									<div>{{ item.endDate | dateFilter }}</div>
								</div>
								<div class="info-row" v-if="item.rejectReason">
									<strong>거부 사유</strong>
									<div class="reject-reason">{{ item.rejectReason }}</div>
								</div>
								<div class="info-row" v-if="item.offlineAuthKey && item.approvalStatus === 'APPROVED'">
									<strong>인증키</strong>
									<div class="auth-key-wrap">
										<input 
											type="text" 
											:value="item.offlineAuthKey" 
											readonly 
											class="auth-key-input"
											:id="'auth-key-' + item.id"
										/>
										<button 
											type="button" 
											class="btn-copy"
											@click="copyAuthKey(item.id, item.offlineAuthKey)"
										>
											복사
										</button>
									</div>
								</div>
							</div>
							<!-- 철회 버튼 (승인 대기 상태일 때만 표시) -->
							<div v-if="item.approvalStatus === 'PENDING'" class="action-area">
								<button 
									type="button" 
									class="btn-cancel-request"
									@click="showCancelConfirmModal(item)"
									:disabled="cancellingId === item.id"
								>
									{{ cancellingId === item.id ? '처리 중...' : '신청 철회' }}
								</button>
							</div>
						</div>
					</li>
				</ul>
				<div v-else class="empty-message">
					<p v-if="totalCount === 0">오프라인 인증 신청 내역이 없습니다.</p>
					<p v-else>선택한 필터 조건에 맞는 신청 내역이 없습니다.</p>
				</div>
			</div>
			
			<!-- Load More 버튼 -->
			<div v-if="!loading && historyList.length > 0 && historyList.length < totalCount" class="btn-area">
				<a href="javascript:;" class="btn-loadmore" @click="doLoadMore">더보기</a>
			</div>

			<!-- 철회 확인 모달 -->
			<div v-if="showCancelModal" class="modal-overlay" @click="closeCancelModal">
				<div class="modal-content" @click.stop>
					<div class="modal-header">
						<h3>신청 철회</h3>
						<button class="modal-close" @click="closeCancelModal">×</button>
					</div>
					<div class="modal-body">
						<p class="cancel-message">정말로 이 오프라인 인증 신청을 철회하시겠습니까?</p>
						<div v-if="cancelTargetItem" class="cancel-info">
							<div class="info-row">
								<strong>제품명:</strong>
								<span>{{ cancelTargetItem.productName }}</span>
							</div>
							<div class="info-row">
								<strong>시리얼 번호:</strong>
								<span>{{ cancelTargetItem.serialNumber }}</span>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button class="btn-cancel" @click="closeCancelModal">취소</button>
						<button 
							class="btn-submit" 
							@click="confirmCancel"
							:disabled="cancellingId !== null"
						>
							{{ cancellingId !== null ? '처리 중...' : '철회하기' }}
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "offlineCertificationHistory",
	data() {
		var params = this.pageUtils.Params();
		return {
			params,
			loading: false,
			historyList: [],
			productNames: [],
			totalCount: 0,
			pageCount: 10,
			filters: {
				productName: "",
				approvalStatus: "",
			},
			showCancelModal: false,
			cancelTargetItem: null,
			cancellingId: null,
		}
	},
	async created() {
		await !this.requireLogin(this, false);
	},
	mounted() {
		this.getHistoryList();
	},
	watch: {
		'filters.productName'() {
			// 필터 변경 시 첫 페이지로 리셋하고 전체 데이터 다시 로드
			this.resetAndReload();
		},
		'filters.approvalStatus'() {
			// 필터 변경 시 첫 페이지로 리셋하고 전체 데이터 다시 로드
			this.resetAndReload();
		}
	},
		methods: {
		getHistoryList() {
			this.loading = true;
			const requestParams = {
				userId: this.getSessionProc().coEmailId,
				page: this.params.page || 0,
				size: this.pageCount,
			};
			
			// 필터 파라미터 추가
			if (this.filters.productName) {
				requestParams.productName = this.filters.productName;
			}
			if (this.filters.approvalStatus) {
				requestParams.approvalStatus = this.filters.approvalStatus;
			}
			
			this.axiosCaller.get(this, this.APIs.LICENSE + "/getMyOfflineCertificationHistory", requestParams, (r) => {
				this.loading = false;
				if (r.data.status === "ok") {
					const newList = r.data.list || [];
					if (this.params.page === 0) {
						// 첫 페이지면 교체
						this.historyList = newList;
					} else {
						// 추가 페이지면 기존 리스트에 추가
						this.historyList = [...this.historyList, ...newList];
					}
					this.totalCount = r.data.count || 0;
					this.productNames = r.data.productNames || [];
				} else {
					this.swalUtils.gritter("Error", r.data.msg || "신청 내역을 불러올 수 없습니다.", "error");
				}
			}, (error) => {
				this.loading = false;
				this.swalUtils.gritter("Error", "신청 내역을 불러오는 중 오류가 발생했습니다.", "error");
			})
		},
		doLoadMore() {
			this.params.page = (this.params.page || 0) + 1;
			this.pageUtils.routerPush(this, this.params);
			this.getHistoryList();
		},
		resetAndReload() {
			this.params.page = 0;
			this.historyList = [];
			this.getHistoryList();
		},
		getStatusLabel(status) {
			if (!status) return "알 수 없음";
			switch(status.toUpperCase()) {
				case "PENDING":
					return "승인 대기";
				case "APPROVED":
					return "승인";
				case "REJECTED":
					return "거부";
				case "CANCELLED":
					return "철회";
				default:
					return status;
			}
		},
		getStatusClass(status) {
			if (!status) return "";
			switch(status.toUpperCase()) {
				case "PENDING":
					return "status-pending";
				case "APPROVED":
					return "status-approved";
				case "REJECTED":
					return "status-rejected";
				case "CANCELLED":
					return "status-cancelled";
				default:
					return "";
			}
		},
		copyAuthKey(id, authKey) {
			const input = document.getElementById('auth-key-' + id);
			if (input) {
				input.select();
				input.setSelectionRange(0, 99999); // 모바일 지원
				try {
					document.execCommand('copy');
					this.swalUtils.gritter("완료", "인증키가 클립보드에 복사되었습니다.", "success", 2000);
				} catch (err) {
					// execCommand가 실패하면 클립보드 API 사용
					if (navigator.clipboard) {
						navigator.clipboard.writeText(authKey).then(() => {
							this.swalUtils.gritter("완료", "인증키가 클립보드에 복사되었습니다.", "success", 2000);
						}).catch(() => {
							this.swalUtils.gritter("오류", "인증키 복사에 실패했습니다.", "error");
						});
					} else {
						this.swalUtils.gritter("오류", "인증키 복사에 실패했습니다.", "error");
					}
				}
			}
		},
		formatDateTime(value) {
			if (!value) return "";
			const date = new Date(value);
			if (isNaN(date.getTime())) return value;
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const day = String(date.getDate()).padStart(2, '0');
			const hours = String(date.getHours()).padStart(2, '0');
			const minutes = String(date.getMinutes()).padStart(2, '0');
			return `${year}-${month}-${day} ${hours}:${minutes}`;
		},
		showCancelConfirmModal(item) {
			this.cancelTargetItem = item;
			this.showCancelModal = true;
		},
		closeCancelModal() {
			this.showCancelModal = false;
			this.cancelTargetItem = null;
		},
		confirmCancel() {
			if (!this.cancelTargetItem) return;
			
			this.cancellingId = this.cancelTargetItem.id;
			
			this.axiosCaller.delete(
				this,
				this.APIs.LICENSE + `/cancelOfflineCertification/${this.cancelTargetItem.id}`,
				{ userId: this.getSessionProc().coEmailId },
				(result) => {
					const data = result?.data || {};
					if (data.status === "ok") {
						this.swalUtils.gritter("완료", "오프라인 인증 신청이 철회되었습니다.", "success", 2000);
						this.closeCancelModal();
						// 목록 새로고침
						this.resetAndReload();
					} else {
						this.swalUtils.gritter("오류", data.msg || "철회에 실패했습니다.", "error", 3000);
					}
					this.cancellingId = null;
				},
				(error) => {
					this.swalUtils.gritter("오류", "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.", "error", 3000);
					this.cancellingId = null;
				}
			);
		}
	}
}
</script>

<style scoped>
.filter-area {
	margin-top: 20px;
	margin-bottom: 20px;
	padding: 20px;
	background: #f9f9f9;
	border-radius: 8px;
}

.filter-row {
	display: flex;
	gap: 20px;
	align-items: flex-end;
}

.filter-item {
	flex: 1;
	min-width: 200px;
}

.filter-item label {
	display: block;
	margin-bottom: 8px;
	font-weight: 600;
	color: #333;
	font-size: 14px;
}

.filter-select {
	width: 100%;
	padding: 10px 12px;
	border: 1px solid #ddd;
	border-radius: 4px;
	font-size: 14px;
	background: #fff;
	cursor: pointer;
	transition: border-color 0.2s;
	text-align: left;
	text-align-last: left;
	direction: ltr;
	appearance: none;
	-webkit-appearance: none;
	-moz-appearance: none;
}

.filter-select:hover {
	border-color: #999;
}

.filter-select:focus {
	outline: none;
	border-color: #2196f3;
	box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.certification-history-list {
	margin-top: 20px;
}

.certification-history-list ul {
	list-style: none;
	padding: 0;
	margin: 0;
}

.history-item {
	background: #fff;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	margin-bottom: 16px;
	padding: 20px;
	transition: box-shadow 0.2s;
}

.history-item:hover {
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.date-wrap {
	font-size: 14px;
	color: #666;
	margin-bottom: 12px;
	padding-bottom: 12px;
	border-bottom: 1px solid #f0f0f0;
}

.content-wrap {
	margin-top: 12px;
}

.info-box {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.info-row {
	display: flex;
	align-items: flex-start;
	gap: 12px;
}

.info-row strong {
	min-width: 120px;
	font-weight: 600;
	color: #333;
	font-size: 14px;
}

.info-row div {
	flex: 1;
	color: #666;
	font-size: 14px;
	word-break: break-word;
}

.status-pending {
	color: #ff9800;
	font-weight: 600;
}

.status-approved {
	color: #2e7d32;
	font-weight: 600;
}

.status-rejected {
	color: #d32f2f;
	font-weight: 600;
}

.status-cancelled {
	color: #757575;
	font-weight: 600;
}

.reject-reason {
	color: #f44336;
	font-style: italic;
}

.auth-key-wrap {
	display: flex;
	gap: 8px;
	align-items: center;
}

.auth-key-input {
	flex: 1;
	padding: 8px 12px;
	border: 1px solid #ddd;
	border-radius: 4px;
	font-size: 12px;
	font-family: monospace;
	background: #f9f9f9;
}

.btn-copy {
	padding: 8px 16px;
	background: #2196f3;
	color: #fff;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	font-size: 14px;
	transition: background 0.2s;
}

.btn-copy:hover {
	background: #1976d2;
}

.action-area {
	margin-top: 16px;
	padding-top: 16px;
	border-top: 1px solid #f0f0f0;
	text-align: right;
}

.btn-cancel-request {
	padding: 10px 24px;
	background: #f44336;
	color: #fff;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	font-size: 14px;
	font-weight: 600;
	transition: background 0.2s;
}

.btn-cancel-request:hover:not(:disabled) {
	background: #d32f2f;
}

.btn-cancel-request:disabled {
	background: #ccc;
	cursor: not-allowed;
}

/* 모달 스타일 */
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.6);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 2000;
	backdrop-filter: blur(2px);
	animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
	from { opacity: 0; }
	to { opacity: 1; }
}

.modal-content {
	background: #fff;
	border-radius: 12px;
	width: 90%;
	max-width: 500px;
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
	animation: slideUp 0.3s ease;
	display: flex;
	flex-direction: column;
}

@keyframes slideUp {
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.modal-header {
	padding: 24px 28px;
	border-bottom: 1px solid #e9ecef;
	display: flex;
	justify-content: space-between;
	align-items: center;
	background: #f8f9fa;
	border-radius: 12px 12px 0 0;
}

.modal-header h3 {
	margin: 0;
	font-size: 22px;
	font-weight: 700;
	color: #212529;
	letter-spacing: -0.5px;
}

.modal-close {
	background: none;
	border: none;
	font-size: 32px;
	color: #6c757d;
	cursor: pointer;
	line-height: 1;
	padding: 0;
	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 6px;
	transition: all 0.2s ease;
}

.modal-close:hover {
	color: #212529;
	background: #e9ecef;
}

.modal-body {
	padding: 28px;
}

.cancel-message {
	font-size: 16px;
	margin-bottom: 20px;
	line-height: 1.7;
	color: #212529;
	font-weight: 500;
}

.cancel-info {
	background: #f8f9fa;
	padding: 16px;
	border-radius: 8px;
	border: 1px solid #e9ecef;
}

.cancel-info .info-row {
	display: flex;
	gap: 12px;
	margin-bottom: 8px;
}

.cancel-info .info-row:last-child {
	margin-bottom: 0;
}

.cancel-info .info-row strong {
	min-width: 100px;
	font-weight: 600;
	color: #333;
	font-size: 14px;
}

.cancel-info .info-row span {
	color: #666;
	font-size: 14px;
}

.modal-footer {
	padding: 20px 28px;
	border-top: 1px solid #e9ecef;
	display: flex;
	gap: 12px;
	justify-content: flex-end;
	background: #f8f9fa;
	border-radius: 0 0 12px 12px;
}

.modal-footer .btn-cancel {
	padding: 12px 32px;
	background: #6c757d;
	color: #fff;
	border: none;
	border-radius: 8px;
	font-size: 15px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
}

.modal-footer .btn-cancel:hover {
	background: #5a6268;
}

.modal-footer .btn-submit {
	padding: 12px 32px;
	background: #f44336;
	color: #fff;
	border: none;
	border-radius: 8px;
	font-size: 15px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
}

.modal-footer .btn-submit:hover:not(:disabled) {
	background: #d32f2f;
}

.modal-footer .btn-submit:disabled {
	background: #ccc;
	cursor: not-allowed;
}

.loading-message {
	text-align: center;
	padding: 40px;
	color: #666;
}

.empty-message {
	text-align: center;
	padding: 60px 20px;
	color: #999;
}

.empty-message p {
	font-size: 16px;
}

.btn-area {
	text-align: center;
	margin-top: 30px;
	margin-bottom: 30px;
}

.btn-loadmore {
	display: inline-block;
	padding: 12px 40px;
	background: #2196f3;
	color: #fff;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	font-size: 14px;
	font-weight: 600;
	text-decoration: none;
	transition: background 0.2s;
}

.btn-loadmore:hover {
	background: #1976d2;
}

@media (max-width: 768px) {
	.filter-area {
		padding: 16px;
	}

	.filter-row {
		flex-direction: column;
		gap: 16px;
	}

	.filter-item {
		min-width: auto;
		width: 100%;
	}

	.filter-select {
		width: 100%;
		text-align: left;
		text-align-last: left;
	}

	.info-row {
		flex-direction: column;
		gap: 4px;
	}

	.info-row strong {
		min-width: auto;
	}

	.auth-key-wrap {
		flex-direction: column;
		align-items: stretch;
	}

	.btn-copy {
		width: 100%;
	}

	.modal-content {
		width: 95%;
		margin: 20px;
	}

	.modal-footer {
		flex-direction: column;
	}

	.modal-footer .btn-cancel,
	.modal-footer .btn-submit {
		width: 100%;
	}
}
</style>

